'use strict';
import get from 'lodash/get';
import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import isUndefined from 'lodash/isUndefined';
import toLower from 'lodash/toLower';
import {getOrganization, getUser} from '../../app/model/reducers/state';
import {getId} from '../domain/ReduxUtils';
import {AwsS3RequestService} from './AwsS3RequestService';


AwsS3GatewayService.$inject = [
  'DeviceServices',
  '$timeout',
  '$http',
  '$ngRedux',
  AwsS3RequestService.SERVICE_NAME,
];

/**
 * @param DeviceServices
 * @param $timeout
 * @param $http
 * @param $ngRedux
 * @param {AwsS3RequestService} awsS3RequestService
 * @return {AwsS3GatewayService}
 * @constructor
 */
export function AwsS3GatewayService(
  DeviceServices,
  $timeout,
  $http,
  $ngRedux,
  awsS3RequestService,
) {
  const service = {
    /* API */

    upload: upload,
    addFile: addFile,

    /* Utility methods */

    doExpBackoff: doExpBackoff,
    isImageFile: isImageFile
  };

  function doExpBackoff(requestFn, tries, initTimeout, retryOnErrorCode) {
    var backoffDeferred = Promise.defer();
    var triesRemaining = tries;
    var currentTimeout = initTimeout;

    if (initTimeout == 0) {
      tryRequest()
    } else {
      $timeout(tryRequest, initTimeout);
    }

    function tryRequest() {
      requestFn().then(onRequestSuccess, isUndefined(retryOnErrorCode) ? onRequestError : onRequestErrorCode);
    }

    function onRequestSuccess(response) {
      if (isObject(response) && response.statusCode == 201) {
        retry();
      } else {
        backoffDeferred.resolve(response);
      }
    }

    function onRequestError(error) {
      if (--triesRemaining > 0) {
        retry();
      } else {
        backoffDeferred.reject(error);
      }
    }

    function onRequestErrorCode(error) {
      var statusCode;
      if (error) {
        if (isNumber(error.statusCode)) {
          statusCode = error.statusCode;
        } else if (isNumber(error.status)) {
          statusCode = error.status;
        } else if (isNumber(error.code)) {
          statusCode = error.code;
        }
      }
      if (statusCode === retryOnErrorCode && --triesRemaining > 0) {
        retry();
      } else {
        backoffDeferred.reject(error);
      }
    }

    function retry() {
      currentTimeout += 2 * currentTimeout + 250;
      $timeout(function () {
        tryRequest();
      }, currentTimeout)
    }

    return backoffDeferred.promise;
  }

  function upload(data) {
    return $http.upload({
      method: 'PUT',
      url: data.url,
      headers: data.headers,
      data: data.file
    });
  }

  function addFile(config) {
    var progressDeferred = Promise.defer();
    var uploadedDeferred = Promise.defer();
    var verifiedDeferred = Promise.defer();
    var verifiedPromise = verifiedDeferred.promise;

    // handle response
    verifiedPromise.uploadProgress = progressDeferred.promise;
    verifiedPromise.uploadComplete = uploadedDeferred.promise;

    verifiedPromise.uploadVerified = function (fn) {
      verifiedPromise.then(function (response) {
        __printResponse(response, 'uploadVerified');
        return fn(response); // TODO: improve response return
      });
      return verifiedPromise;
    };

    __verifyConfig(config, true);
    if (!config.file) {
      var error = new Error('addFile.config: A file is required.');
      uploadedDeferred.reject(error);
      verifiedDeferred.reject(error);
      return verifiedPromise;
    } else {
      __beginUpload(config, uploadedDeferred, verifiedDeferred, progressDeferred);
    }

    return verifiedPromise;
  }

  function __beginUpload(_config, uploadedDeferred, verifiedDeferred, progressDeferred) {
    var config = {...(_config || {})};
    var file, fileUri;

    file = config.file;
    delete config.file;

    fileUri = config.fileUri || null;
    delete config.fileUri;

    var awaitUploadAuthorization = __getUploadAuthorization(config, file);
    var uploadFile = awaitUploadAuthorization.then(__createPerformUpload(config, file, fileUri, uploadedDeferred, verifiedDeferred, progressDeferred));

    uploadFile.catch(function (error) {
      uploadedDeferred.reject(error);
      verifiedDeferred.reject(error);
    });
  }

  function __getUploadAuthorization(config, file) {
    const rootState = $ngRedux.getState();
    const organization = getOrganization(rootState);
    const userId = getId(getUser(rootState));
    const uploadMethod = 'put';
    const authorizationConfig = {
      ...(config || {}),
      method: uploadMethod,
      headers: __getHeaders(file),
      user: userId,
      org: getId(organization),
    };

    if (authorizationConfig.file) {
      delete authorizationConfig.file;
    }

    return awsS3RequestService.requestUploadUrl(authorizationConfig);
  }

  function __createPerformUpload(config, file, fileUri, uploadedDeferred, verifiedDeferred, progressDeferred) {
    return function (responseData) {
      var fileId = responseData.firebaseId;
      var fileKey = responseData.name;

      var uploadConfig = {
        file: file,
        fileUri: fileUri || null,
        ...responseData,
      };

      var result = service.upload(uploadConfig);

      progressDeferred.resolve(result.observable);

      return result.promise.then(
        __createOnUploadSuccessHandler(config, fileId, fileKey, uploadedDeferred),
        __createOnUploadErrorHandler(config, fileKey, uploadedDeferred, verifiedDeferred))
        .then(function (response) {
          verifiedDeferred.resolve({fileId: fileId, ...response});
          return response;
        });
    }
  }

  function __createOnUploadSuccessHandler(config, fileId, fileKey, uploadedDeferred) {
    return function onUploadSuccessHandler() {
      uploadedDeferred.resolve({fileId}); // todo: improve response handling
      const successNotifyConfig = {
        ...(config || {}),
        name: fileKey,
        path: get((config || {}), 'path', ''),
      };

      return awsS3RequestService.reportUploadSuccess(successNotifyConfig);
    }
  }

  function __createOnUploadErrorHandler(config, fileKey, uploadedDeferred, verifiedDeferred) {
    return function onUploadErrorHandler(response) {
      __printResponse(response, 'AWS:S3 Upload');
      uploadedDeferred.reject(response);
      verifiedDeferred.reject(response);
      return awsS3RequestService
        .reportUploadError({
          ...(config || {}),
          name: fileKey,
          path: get((config || {}), 'path', ''),
        });
    }
  }

  // Internal methods

  //TODO: add support for verbs...
  function __verifyConfig(config, allowNullName) {
    if (!isObject(config)) throw Error('AwsS3GatewayService request data must be an object.  Received: ', config);
    if (!isString(config.path)) throw new Error('addFile.config: A path is required.');
    if (!allowNullName && !config.name) throw new Error('addFile.config: A name is required.');
  }

  // TODO: calculate MD5 hash and add Content-MD5 header for integrity check.
  function __getHeaders(file) {
    // HACK: this ust assumes jpeg to get around iOS Safari not attempting to determine the mime type apparently
    return file.type ? {
      'Content-Type': file.type
    } : {}; //  || 'image/jpeg'
  }

  function __printResponse(response, responseFor) {
    //responseFor = responseFor || '';
    //console.log(responseFor + ' response:', response);
    return response;
  }

  return service;
}

export function isImageFile(file) {
  if (!isObject(file))
    return false;
  if (file.type && file.type.indexOf('image') > -1)
    return true;
  if (!file.type || (file.type && file.type.indexOf('octet') > -1)) {
    const name = file.name || file.fileUri;
    if (!isString(name))
      return false;

    const ext = toLower(name.substring(name.lastIndexOf('.') + 1));

    //console.log('missing type, falling back to extension', ext);

    switch (ext) {
      case 'gif':
      case 'png':
      case 'jpeg':
      case 'jpg':
      case 'tiff':
        return true;
    }
  }
  return false;
}



// WEBPACK FOOTER //
// ./src/js/core-utilities/aws-api/AwsS3GatewayService.js